import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type Paginated, type ApiSuccess } from '../../lib/api';

interface Invitation {
  id: string;
  email: string;
  name: string | null;
  role: string;
  verificationTier: string | null;
  status: 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'REVOKED';
  message: string | null;
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
}

interface SignupCode {
  id: string;
  code: string;
  label: string | null;
  defaultRole: string;
  defaultTier: string | null;
  maxUses: number | null;
  usedCount: number;
  expiresAt: string | null;
  active: boolean;
  createdAt: string;
}

const STATUS: Record<string, { label: string; color: 'amber' | 'green' | 'red' | 'navy' }> = {
  PENDING:  { label: 'معلّقة', color: 'amber' },
  ACCEPTED: { label: 'تم القبول', color: 'green' },
  EXPIRED:  { label: 'منتهية', color: 'red' },
  REVOKED:  { label: 'ملغاة', color: 'red' },
};

export function AdminInvitationsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'invites' | 'codes'>('invites');
  const [openInvite, setOpenInvite] = useState(false);
  const [openCode, setOpenCode] = useState(false);

  const invites = useQuery({
    queryKey: ['admin', 'invitations'],
    queryFn: () => api.get<Paginated<Invitation>>('/api/admin/invitations'),
  });
  const codes = useQuery({
    queryKey: ['admin', 'signup-codes'],
    queryFn: () => api.get<ApiSuccess<SignupCode[]>>('/api/admin/signup-codes'),
  });

  const revokeM = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/invitations/${id}/revoke`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'invitations'] }),
  });
  const resendM = useMutation({
    mutationFn: (id: string) => api.post<ApiSuccess<{ link: string }>>(`/api/admin/invitations/${id}/resend`, {}),
    onSuccess: (r) => { alert(`الرابط الجديد:\n${r.data.link}`); qc.invalidateQueries({ queryKey: ['admin', 'invitations'] }); },
  });

  const toggleCodeM = useMutation({
    mutationFn: (id: string) => api.put(`/api/admin/signup-codes/${id}/toggle`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'signup-codes'] }),
  });
  const deleteCodeM = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/signup-codes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'signup-codes'] }),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-black text-navy-dark">📨 الدعوات وأكواد التسجيل</h1>
        <div className="flex gap-2">
          {tab === 'invites'
            ? <Button variant="primary" onClick={() => setOpenInvite(true)}>+ إرسال دعوة</Button>
            : <Button variant="primary" onClick={() => setOpenCode(true)}>+ إنشاء كود تسجيل</Button>
          }
        </div>
      </div>

      <div className="flex gap-2 mb-4 border-b border-gray-200">
        <TabBtn active={tab === 'invites'} onClick={() => setTab('invites')}>📧 دعوات الإيميل</TabBtn>
        <TabBtn active={tab === 'codes'}   onClick={() => setTab('codes')}>🔖 أكواد التسجيل</TabBtn>
      </div>

      {tab === 'invites' && (
        <Card>
          <CardBody className="!p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <Th>المدعو</Th><Th>الدور</Th><Th>الحالة</Th><Th>تنتهي</Th><Th>الإجراءات</Th>
                </tr>
              </thead>
              <tbody>
                {invites.isLoading ? (
                  <tr><td colSpan={5} className="text-center py-12 text-gray-500">⟳ جاري التحميل…</td></tr>
                ) : !invites.data?.data?.length ? (
                  <tr><td colSpan={5} className="text-center py-12 text-gray-500">لا توجد دعوات</td></tr>
                ) : invites.data.data.map((i) => (
                  <tr key={i.id} className="border-b border-gray-100">
                    <td className="px-4 py-3">
                      <div className="font-bold">{i.name || '—'}</div>
                      <div className="text-xs text-gray-500" dir="ltr">{i.email}</div>
                    </td>
                    <td className="px-4 py-3"><Badge color={i.role === 'ADMIN' ? 'gold' : 'navy'}>{i.role}</Badge></td>
                    <td className="px-4 py-3"><Badge color={STATUS[i.status].color}>{STATUS[i.status].label}</Badge></td>
                    <td className="px-4 py-3 text-xs text-gray-500">{new Date(i.expiresAt).toLocaleDateString('ar-SA')}</td>
                    <td className="px-4 py-3">
                      {i.status === 'PENDING' && (
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => resendM.mutate(i.id)}>🔁</Button>
                          <Button size="sm" variant="ghost" onClick={() => revokeM.mutate(i.id)}>✕</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}

      {tab === 'codes' && (
        <Card>
          <CardBody className="!p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <Th>الكود</Th><Th>الوصف</Th><Th>الدور</Th><Th>الاستخدامات</Th><Th>الحالة</Th><Th>الإجراءات</Th>
                </tr>
              </thead>
              <tbody>
                {codes.isLoading ? (
                  <tr><td colSpan={6} className="text-center py-12 text-gray-500">⟳ جاري التحميل…</td></tr>
                ) : !codes.data?.data?.length ? (
                  <tr><td colSpan={6} className="text-center py-12 text-gray-500">لا توجد أكواد</td></tr>
                ) : codes.data.data.map((co) => (
                  <tr key={co.id} className="border-b border-gray-100">
                    <td className="px-4 py-3 font-mono font-black text-navy-dark" dir="ltr">{co.code}</td>
                    <td className="px-4 py-3 text-sm">{co.label || '—'}</td>
                    <td className="px-4 py-3"><Badge color="navy">{co.defaultRole}</Badge></td>
                    <td className="px-4 py-3 text-sm">{co.usedCount}{co.maxUses ? `/${co.maxUses}` : ''}</td>
                    <td className="px-4 py-3">
                      <Badge color={co.active ? 'green' : 'red'}>{co.active ? 'نشط' : 'موقوف'}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => toggleCodeM.mutate(co.id)}>{co.active ? '⏸' : '▶'}</Button>
                        <Button size="sm" variant="ghost" onClick={() => { if (confirm(`حذف ${co.code}؟`)) deleteCodeM.mutate(co.id); }}>🗑</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}

      {openInvite && <CreateInviteModal onClose={() => setOpenInvite(false)} onCreated={() => qc.invalidateQueries({ queryKey: ['admin', 'invitations'] })} />}
      {openCode && <CreateCodeModal onClose={() => setOpenCode(false)} onCreated={() => qc.invalidateQueries({ queryKey: ['admin', 'signup-codes'] })} />}
    </div>
  );
}

function CreateInviteModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [f, setF] = useState({ email: '', name: '', role: 'USER', verificationTier: 'NONE', message: '', expiresInDays: 14 });
  const [err, setErr] = useState('');
  const [link, setLink] = useState('');

  const m = useMutation({
    mutationFn: () => api.post<ApiSuccess<{ link: string }>>('/api/admin/invitations', {
      ...f,
      verificationTier: f.verificationTier === 'NONE' ? undefined : f.verificationTier,
      name: f.name.trim() || undefined,
      message: f.message.trim() || undefined,
    }),
    onSuccess: (r) => { setLink(r.data.link); onCreated(); },
    onError: (e: Error) => setErr(e.message),
  });

  if (link) return (
    <Modal title="✓ تم إرسال الدعوة" onClose={onClose}>
      <p className="text-sm text-gray-600 mb-3">أرسل هذا الرابط للمدعو:</p>
      <div className="bg-gray-100 rounded-xl p-3 text-xs font-mono break-all" dir="ltr">{link}</div>
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="ghost" onClick={() => navigator.clipboard.writeText(link)}>نسخ</Button>
        <Button variant="primary" onClick={onClose}>إغلاق</Button>
      </div>
    </Modal>
  );

  return (
    <Modal title="📧 إرسال دعوة جديدة" onClose={onClose}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input label="الإيميل *" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} dir="ltr" />
        <Input label="الاسم" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <div>
          <label className="block text-sm font-bold text-ink mb-1">الدور</label>
          <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
            <option value="USER">عميل</option>
            <option value="BROKER">مسوّق</option>
            <option value="ADMIN">إداري</option>
          </select>
        </div>
        <Input label="ينتهي خلال (يوم)" type="number" value={String(f.expiresInDays)} onChange={(e) => setF({ ...f, expiresInDays: Number(e.target.value) })} />
      </div>
      <div className="mt-3">
        <label className="block text-sm font-bold text-ink mb-1">رسالة شخصية (اختياري)</label>
        <textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} rows={3} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white" />
      </div>
      {err && <div className="text-sm text-red-600 mt-2">⛔ {err}</div>}
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="ghost" onClick={onClose}>إلغاء</Button>
        <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending} disabled={!f.email}>إرسال</Button>
      </div>
    </Modal>
  );
}

function CreateCodeModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [f, setF] = useState({ code: '', label: '', defaultRole: 'USER', defaultTier: 'NONE', maxUses: '', expiresInDays: '' });
  const [err, setErr] = useState('');

  const m = useMutation({
    mutationFn: () => api.post('/api/admin/signup-codes', {
      ...f,
      defaultTier: f.defaultTier === 'NONE' ? undefined : f.defaultTier,
      maxUses: f.maxUses ? Number(f.maxUses) : undefined,
      expiresInDays: f.expiresInDays ? Number(f.expiresInDays) : undefined,
    }),
    onSuccess: () => { onCreated(); onClose(); },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <Modal title="🔖 كود تسجيل جديد" onClose={onClose}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input label="الكود *" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} dir="ltr" placeholder="WELCOME2026" />
        <Input label="وصف داخلي" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
        <div>
          <label className="block text-sm font-bold text-ink mb-1">الدور الافتراضي</label>
          <select value={f.defaultRole} onChange={(e) => setF({ ...f, defaultRole: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
            <option value="USER">عميل</option>
            <option value="BROKER">مسوّق</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-bold text-ink mb-1">مستوى التوثيق</label>
          <select value={f.defaultTier} onChange={(e) => setF({ ...f, defaultTier: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
            <option value="NONE">بدون</option>
            <option value="GREEN">أخضر</option>
            <option value="GOLD">ذهبي</option>
            <option value="DIAMOND">ماسي</option>
          </select>
        </div>
        <Input label="حد الاستخدامات" type="number" value={f.maxUses} onChange={(e) => setF({ ...f, maxUses: e.target.value })} placeholder="فارغ = غير محدود" />
        <Input label="ينتهي خلال (يوم)" type="number" value={f.expiresInDays} onChange={(e) => setF({ ...f, expiresInDays: e.target.value })} placeholder="فارغ = دائم" />
      </div>
      {err && <div className="text-sm text-red-600 mt-2">⛔ {err}</div>}
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="ghost" onClick={onClose}>إلغاء</Button>
        <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending} disabled={!f.code}>إنشاء</Button>
      </div>
    </Modal>
  );
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-black text-navy-dark">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function TabBtn({ children, active, onClick }: { children: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={`px-4 py-2 text-sm font-bold transition border-b-2 -mb-px ${
      active ? 'border-gold text-navy-dark' : 'border-transparent text-gray-500 hover:text-navy-dark'
    }`}>{children}</button>
  );
}
function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-right px-4 py-3 font-bold text-gray-600 text-xs uppercase">{children}</th>;
}
